/**
 * Device Fingerprinting
 * 
 * Generates a unique device ID for security and session management
 */

interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  timezone: string;
  screen: {
    width: number;
    height: number;
    colorDepth: number;
  };
  hardwareConcurrency: number;
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
  };
}

class DeviceFingerprint {
  private fingerprint: string | null = null;

  /**
   * Generate device fingerprint
   */
  async generate(): Promise<string> {
    if (this.fingerprint) {
      return this.fingerprint;
    }

    const info = this.collectDeviceInfo();
    const hash = await this.hashDeviceInfo(info);
    
    this.fingerprint = hash;
    return hash;
  }

  /**
   * Collect device information
   */
  private collectDeviceInfo(): DeviceInfo {
    const nav = navigator as Navigator & {
      deviceMemory?: number;
      connection?: {
        effectiveType?: string;
        downlink?: number;
      };
    };

    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
      },
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: nav.deviceMemory,
      connection: nav.connection ? {
        effectiveType: nav.connection.effectiveType,
        downlink: nav.connection.downlink,
      } : undefined,
    };
  }

  /**
   * Hash device info using SubtleCrypto
   */
  private async hashDeviceInfo(info: DeviceInfo): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(info));
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex.substring(0, 32); // Use first 32 chars
  }

  /**
   * Get stored fingerprint
   */
  get(): string | null {
    return this.fingerprint;
  }

  /**
   * Clear fingerprint (for testing)
   */
  clear(): void {
    this.fingerprint = null;
  }
}

export const deviceFingerprint = new DeviceFingerprint();


















